import { useState, useRef, useEffect } from "react";
import { Link, useLocation, useNavigate } from "react-router-dom";
import Cookies from "js-cookie";
import { useDispatch, useSelector } from "react-redux";
import { GetLogo, LogOut } from "../apis/api/AuthApi";
import { toast } from "react-toastify";
import { motion, AnimatePresence } from "framer-motion";
import useUnreadMessages from "../Hooks/useUnreadMessages";
import "./TopNav.css";

const menuConfig = (access) => [
  { key: "dashboard", label: "Dashboard", icon: "bx bx-home-circle", to: "/dashboard" },
  ...(access.attend_event === "1" || access.attend_event === 1 ? [{
    key: "events", label: "Events", icon: "bx bx-calendar-event",
    mega: {
      cols: [{ heading: "Events", items: [{ icon: "bx bx-calendar", label: "Event List", desc: "Browse all upcoming events", to: "/EventsMenu" }] }],
      promo: { title: "Join Events", body: "Connect with members at live events worldwide.", icon: "bx bx-globe" }
    }
  }] : []),
  {
    key: "nmm", label: "NMM", icon: "bx bx-grid-alt",
    mega: {
      cols: [
        { heading: "News & Plans", items: [
          { icon: "bx bx-news", label: "News", desc: "Latest industry news", to: "/news" },
        ]},
        { heading: "Business", items: [
          { icon: "bx bx-trending-up", label: "View Impact", desc: "See your business impact", to: "/Businessimpact" },
          { icon: "bx bx-transfer", label: "Make Transaction", desc: "Record a new transaction", to: "/Business_impact" },
          { icon: "bx bx-money-withdraw", label: "Withdrawal Request", desc: "Request a withdrawal", to: "/WithdrawalForm" },
        ]},
      ],
      promo: { title: "Business Impact", body: "Track transactions and grow your network.", icon: "bx bx-bar-chart-alt-2" }
    }
  },
  {
    key: "freight", label: "Freight Request", icon: "bx bx-package",
    mega: {
      cols: [{ heading: "Freight", items: [
        { icon: "bx bx-plus-circle", label: "New Request", desc: "Create a freight request", to: "/new_request" },
        { icon: "bx bx-send", label: "Request Sent", desc: "Track sent requests", to: "/sent_request" },
        { icon: "bx bx-download", label: "Request Received", desc: "View received requests", to: "/receive_request" },
        { icon: "bx bx-check-circle", label: "Request Accept", desc: "Manage accepted requests", to: "/accept_request" },
      ]}],
      promo: { title: "Freight Network", body: "Manage all your freight requests in one place.", icon: "bx bx-world" }
    }
  },
  {
    key: "members", label: "Members", icon: "bx bx-group",
    mega: {
      cols: [{ heading: "User & Members", items: [
        { icon: "bx bx-user-circle", label: "My Profile", desc: "View & edit your profile", to: "/profile" },
        { icon: "bx bx-list-ul", label: "User List", desc: "All registered members", to: "/user" },
        { icon: "bx bx-user-plus", label: "Recent Members", desc: "Newly joined members", to: "/recentMember" },
      ]}],
      promo: { title: "Member Directory", body: "Connect with thousands of members globally.", icon: "bx bx-buildings" }
    }
  },
  {
    key: "invoices", label: "Invoices", icon: "bx bx-receipt",
    mega: {
      cols: [{ heading: "Invoices", items: [
        { icon: "bx bx-upload", label: "Upload Invoice", desc: "Upload a new invoice", to: "/upload-invoices" },
        { icon: "bx bx-list-check", label: "Invoice List", desc: "View all invoices", to: "/invoicelist" },
      ]}],
      promo: { title: "Invoice Manager", body: "Keep all your invoices organised and accessible.", icon: "bx bx-file" }
    }
  },
  {
    key: "community", label: "Community", icon: "bx bx-conversation",
    mega: {
      cols: [{ heading: "Community", items: [
        { icon: "bx bx-time-five", label: "Timeline", desc: "See the community feed", to: "/Timeline" },
        { icon: "bx bx-chat", label: "Chat", desc: "Direct member messaging", to: "/chat" },
        { icon: "bx bx-info-circle", label: "Help", desc: "Support & documentation", to: "/Help" },
      ]}],
      promo: { title: "Stay Connected", body: "Engage with the community through chat and timeline.", icon: "bx bx-network-chart" }
    }
  },
];

const TopNav = () => {
  const location = useLocation();
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const [activeMenu, setActiveMenu] = useState(null);
  const [userOpen, setUserOpen] = useState(false);
  const [mobileOpen, setMobileOpen] = useState(false);
  const [mobileExpanded, setMobileExpanded] = useState(null);
  const navRef = useRef(null);
  const timerRef = useRef(null);

  const access = JSON.parse(Cookies.get("access") || "{}");
  const user = JSON.parse(Cookies.get("user") || "{}");
  const companyLogo = useSelector((s) => s.AuthSlice?.companyLogo);
  const menus = menuConfig(access);
  const totalUnread = useUnreadMessages();

  const isActive = (path) => location.pathname === path;
  const isGroupActive = (cfg) => {
    if (cfg.to) return isActive(cfg.to);
    return cfg.mega?.cols?.some((col) => col.items.some((i) => location.pathname.startsWith(i.to)));
  };

  const openMenu = (key) => { clearTimeout(timerRef.current); setActiveMenu(key); };
  const closeMenu = () => { timerRef.current = setTimeout(() => setActiveMenu(null), 120); };
  const keepOpen = () => clearTimeout(timerRef.current);

  useEffect(() => {
    setMobileOpen(false);
    setActiveMenu(null);
    setUserOpen(false);
  }, [location.pathname]);

  useEffect(() => {
    const handler = (e) => {
      if (navRef.current && !navRef.current.contains(e.target)) {
        setActiveMenu(null);
        setUserOpen(false);
      }
    };
    document.addEventListener("mousedown", handler);
    return () => document.removeEventListener("mousedown", handler);
  }, []);

  useEffect(() => {
    if (!companyLogo) dispatch(GetLogo());
  }, [dispatch, companyLogo]);

  const handleLogout = async (e) => {
    e.preventDefault();
    try {
      // Clear all cookies immediately (works for both normal and Google login)
      Object.keys(Cookies.get()).forEach((c) => Cookies.remove(c));
      
      // Also try the backend logout (for session cleanup) — but don't block on it
      const userId = Cookies.get("userID");
      dispatch(LogOut({ userId })).catch(() => {});
      
      toast.success("Logout Successful");
      navigate("/");
    } catch (err) {
      // Even if something fails, clear cookies and redirect
      Object.keys(Cookies.get()).forEach((c) => Cookies.remove(c));
      navigate("/");
    }
  };

  return (
    <nav className="tn-nav" ref={navRef}>
      <div className="tn-nav-inner">
      {/* Brand */}
      <Link to="/dashboard" className="tn-brand">
        <span className="tn-brand-logo-wrap">
          <img
            src={
              companyLogo
                ? companyLogo.replace("/thumb", "")
                : `${process.env.PUBLIC_URL}/company-logo-design.jpg`
            }
            alt="Logo"
            className="tn-brand-logo-img"
            onError={(e) => {
              e.currentTarget.onerror = null;
              e.currentTarget.src = `${process.env.PUBLIC_URL}/company-logo-design.jpg`;
            }}
          />
        </span>
      </Link>

      {/* Desktop menu */}
      <ul className="tn-menu">
        {menus.map((item) => (
          <li
            key={item.key}
            className={`tn-item ${activeMenu === item.key ? "tn-open" : ""} ${isGroupActive(item) ? "tn-active-item" : ""}`}
            onMouseEnter={() => item.mega && openMenu(item.key)}
            onMouseLeave={() => item.mega && closeMenu()}
          >
            {item.to ? (
              <Link to={item.to} className={`tn-link ${isActive(item.to) ? "tn-active" : ""}`}>
                <i className={item.icon}></i><span>{item.label}</span>
              </Link>
            ) : (
              <button className="tn-link" onClick={() => openMenu(activeMenu === item.key ? null : item.key)}>
               <i className={item.icon}></i>
                <span className="tn-link-label-wrap">
                  {item.label}
                  {item.key === "community" && totalUnread > 0 && (
                    <span className="tn-notif-dot" />
                  )}
                </span>
                <i className="bx bx-chevron-down tn-caret"></i>
              </button>
            )}

            {/* Mega dropdown */}
            {item.mega && activeMenu === item.key && (
              <AnimatePresence>
                <motion.div className="tn-mega"
                  key="mega"
                  initial={{ opacity:0, y:-8 }}
                  animate={{ opacity:1, y:0  }}
                  exit={{    opacity:0, y:-6 }}
                  transition={{ duration:.22, ease:[.22,.68,0,1.2] }}
                  onMouseEnter={keepOpen} onMouseLeave={closeMenu}>
                  <div className="tn-mega-inner">
                    <div className="tn-mega-cols">
                      {item.mega.cols.map((col, ci) => (
                        <div key={ci} className="tn-mega-col">
                          <p className="tn-col-heading">{col.heading}</p>
                          {col.items.map((it, ii) => (
                            <motion.div key={it.to}
                              initial={{ opacity:0, x:-8 }}
                              animate={{ opacity:1, x:0 }}
                              transition={{ delay: ii * 0.05 + ci * 0.04, duration:.22 }}>
                              <Link to={it.to} className={`tn-mega-item ${isActive(it.to) ? "tn-active" : ""}`}>
                                <span className="tn-mega-icon"><i className={it.icon}></i></span>
                                <span className="tn-mega-text">
                                  <span className="tn-mega-label">
                                    {it.label}
                                    {it.to === "/chat" && totalUnread > 0 && (
                                      <span className="tn-notif-dot tn-notif-dot--inline" />
                                    )}
                                  </span>
                                  <span className="tn-mega-desc">{it.desc}</span>
                                </span>
                                <i className="bx bx-arrow-back tn-mega-arrow"></i>
                              </Link>
                            </motion.div>
                          ))}
                        </div>
                      ))}
                    </div>
                    {item.mega.promo && (
                      <motion.div className="tn-mega-promo"
                        initial={{ opacity:0, x:16 }}
                        animate={{ opacity:1, x:0 }}
                        transition={{ duration:.28, delay:.08 }}>
                        <span className="tn-promo-icon"><i className={item.mega.promo.icon}></i></span>
                        <h4 className="tn-promo-title">{item.mega.promo.title}</h4>
                        <p className="tn-promo-body">{item.mega.promo.body}</p>
                      </motion.div>
                    )}
                  </div>
                </motion.div>
              </AnimatePresence>
            )}
          </li>
        ))}
      </ul>

      {/* Right: user */}
      <div className="tn-right">
        <div className={`tn-user ${userOpen ? "tn-open" : ""}`}>
          <button className="tn-user-btn" onClick={() => setUserOpen((p) => !p)}>
            <img
              src={user?.member_logo || `${process.env.PUBLIC_URL}/gray-avatar-icon-vector-illustration_276184-163.avif`}
              alt="avatar" className="tn-avatar"
              onError={(e) => { e.target.onerror = null; e.target.src = `${process.env.PUBLIC_URL}/gray-avatar-icon-vector-illustration_276184-163.avif`; }}
            />
            <div className="tn-user-info">
              <span className="tn-user-name">{user?.fullname || "Guest"}</span>
              <span className="tn-user-co">{user?.company_name || ""}</span>
            </div>
            <i className="bx bx-chevron-down tn-caret"></i>
          </button>
          {userOpen && (
            <AnimatePresence>
              <motion.div className="tn-user-drop"
                key="userdrop"
                initial={{ opacity:0, y:-8, scale:.96 }}
                animate={{ opacity:1, y:0,  scale:1 }}
                exit={{    opacity:0, y:-6, scale:.96 }}
                transition={{ duration:.2, ease:[.22,.68,0,1.2] }}>
                <Link to="/profile" className="tn-drop-item"><i className="bx bx-user"></i> Profile</Link>
                <Link to="/dashboard" className="tn-drop-item"><i className="bx bx-home-circle"></i> Dashboard</Link>
                <div className="tn-drop-divider"></div>
                <button className="tn-drop-item tn-drop-logout" onClick={handleLogout}>
                  <i className="bx bx-log-out-circle"></i> Logout
                </button>
              </motion.div>
            </AnimatePresence>
          )}
        </div>

        {/* Hamburger */}
        <button className="tn-hamburger" onClick={() => setMobileOpen((p) => !p)}>
          <i className={`bx ${mobileOpen ? "bx-x" : "bx-menu"}`}></i>
        </button>
      </div>
      </div> {/* end tn-nav-inner */}

      {/* Mobile drawer */}
      {mobileOpen && (
        <div className="tn-mobile">
          {menus.map((item) => item.to ? (
            <Link key={item.key} to={item.to} className={`tn-mob-link ${isActive(item.to) ? "tn-active" : ""}`}>
              <i className={item.icon}></i> {item.label}
            </Link>
          ) : (
            <div key={item.key}>
              <button className="tn-mob-group" onClick={() => setMobileExpanded((p) => p === item.key ? null : item.key)}>
                <i className={item.icon}></i> {item.label}
                <i className={`bx bx-chevron-${mobileExpanded === item.key ? "up" : "down"} ms-auto`}></i>
              </button>
              {mobileExpanded === item.key && (
                <div className="tn-mob-sub">
                  {item.mega.cols.map((col) => col.items.map((it) => (
                    <Link key={it.to} to={it.to} className={`tn-mob-link tn-mob-sub-link ${isActive(it.to) ? "tn-active" : ""}`}>
                      <i className={it.icon}></i> {it.label}
                                                                  {it.to === "/chat" && totalUnread > 0 && (
                        <span className="tn-notif-dot tn-notif-dot--inline" />
                      )}
                    </Link>
                  )))}
                </div>
              )}
            </div>
          ))}
        </div>
      )}
    </nav>
  );
};

export default TopNav;
